/**
 * Locale-safe utilities for shortening user-generated text into a single
 * preview line. Used by the realtime in-app toast layer so chat message
 * bodies and notification previews are clipped without mid-grapheme cuts
 * or Latin-text-only assumptions.
 *
 * The truncator counts code points via the `Intl.Segmenter` grapheme
 * iterator when it is available (modern Chrome / Safari / Edge / Node 18+),
 * falling back to a `String.from(...)` array iteration that respects
 * surrogate pairs but not full grapheme clusters. This avoids splitting
 * Japanese / Korean text in the middle of a code point or breaking emoji
 * compositions when the platform lacks `Intl.Segmenter`.
 */

/**
 * Convert the message body to a one-line preview by collapsing runs of
 * whitespace and stripping leading/trailing whitespace. Does not strip
 * markup; chat bodies are already plain text on the wire.
 */
export const collapseWhitespace = (input: string): string => {
  if (!input) {
    return "";
  }
  return input.replace(/\s+/g, " ").trim();
};

/**
 * Return up to `maxGraphemes` user-perceived characters from `input`,
 * appending a single horizontal ellipsis when truncation occurs. Safe
 * for Latin, CJK, Hangul, and most emoji sequences.
 *
 * @param input - The text to truncate
 * @param maxGraphemes - Maximum number of user-perceived characters to keep (default 120)
 * @returns The truncated preview string (may equal `input` if it already fits)
 */
export const truncatePreview = (input: string, maxGraphemes = 120): string => {
  const collapsed = collapseWhitespace(input);
  if (!collapsed) {
    return "";
  }
  if (maxGraphemes <= 0) {
    return "";
  }

  // Prefer Intl.Segmenter for true grapheme-cluster boundaries when the
  // platform supports it. The runtime may not have the global typing in
  // older TypeScript libs, so we feature-detect via a narrow cast.
  // We materialize the iterator with `Array.from(...)` instead of a
  // `for...of` loop so the file compiles cleanly under CRA's TypeScript
  // target (no `--downlevelIteration`).
  const segmenterCtor = (globalThis as { Intl?: { Segmenter?: typeof Intl.Segmenter } }).Intl?.Segmenter;
  if (typeof segmenterCtor === "function") {
    const segmenter = new segmenterCtor(undefined, { granularity: "grapheme" });
    const segments = Array.from(
      segmenter.segment(collapsed),
      (segment) => segment.segment,
    );
    if (segments.length <= maxGraphemes) {
      return collapsed;
    }
    return `${segments.slice(0, maxGraphemes).join("")}\u2026`;
  }

  // Fallback: spread into code points so surrogate pairs (most emoji,
  // non-BMP CJK extensions) at least stay intact.
  const codePoints = Array.from(collapsed);
  if (codePoints.length <= maxGraphemes) {
    return collapsed;
  }
  return `${codePoints.slice(0, maxGraphemes).join("")}\u2026`;
};

/**
 * Replace named `{placeholder}` slots in a localized template with the
 * provided values. Mirrors the inline helper in `chatInboxUi.ts` and is
 * exported here so the realtime toast layer can interpolate aria-label
 * templates without re-importing the chat helper from non-chat surfaces.
 */
export const formatTemplate = (
  template: string,
  values: Record<string, number | string>,
): string => {
  return Object.entries(values).reduce(
    (result, [key, value]) => result.replace(`{${key}}`, String(value)),
    template,
  );
};
